import React from 'react';
import { useNavigate } from 'react-router-dom';
import ContactLogo from '../components/shared/ContactLogo';
import HeroDither from '../components/shared/HeroDither';
import Reveal, { REVEAL_CSS } from '../components/shared/Reveal';
import useSeoMeta from '../components/shared/useSeoMeta';
import RelatedPagesLinks from '../components/shared/RelatedPagesLinks';

const IconArrowLeft = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M19 12H5M12 5l-7 7 7 7"/>
  </svg>
);

const IconArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

const IconWallet = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12V7H5a2 2 0 010-4h14v4"/><path d="M3 5v14a2 2 0 002 2h16v-5"/><path d="M18 12a2 2 0 000 4h4v-4z"/>
  </svg>
);

const IconPhoneOff = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2"/><path d="M12 18h.01"/>
  </svg>
);

const IconFree = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 12v8H4v-8M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/>
  </svg>
);

const IconQr = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><path d="M14 14h3v3h-3zM19 14h2v2h-2zM14 19h2v2h-2zM19 19h2v2h-2z"/>
  </svg>
);

const WALLET_CARDS = [
  { icon: <IconWallet/>, title: 'Apple Wallet y Google Wallet', desc: 'Guarda tu perfil directo en la billetera nativa de tu celular, sea cual sea el sistema que uses.' },
  { icon: <IconPhoneOff/>, title: 'Sin abrir ninguna app', desc: 'Una vez guardado, el pase vive en tu billetera. No necesitas abrir Contact para mostrarlo.' },
  { icon: <IconQr/>, title: 'Incluye tu código QR', desc: 'Tu pase lleva tu QR, además de tu nombre, cargo y empresa, para que te identifiquen con solo apuntar la cámara.' },
  { icon: <IconFree/>, title: 'Incluido en el plan gratuito', desc: 'No es una función exclusiva de un plan superior. Está disponible para cualquier cuenta de Contact.' },
];

const FAQS = [
  { q: '¿Funciona igual en iPhone y Android?', a: 'Sí, el mismo perfil se puede guardar en Apple Wallet o en Google Wallet según tu celular.' },
  { q: '¿Tiene costo adicional?', a: 'No, está incluido en el plan gratuito de Contact.' },
  { q: '¿Qué información incluye el pase?', a: 'Tu nombre, cargo, empresa y tu código QR, para que te identifiquen apuntando la cámara.' },
];

const PAGE_TITLE = 'Perfil Digital en Apple Wallet y Google Wallet | Contact';
const PAGE_DESCRIPTION = 'Guarda tu tarjeta de presentación digital en la billetera de tu celular. Sin apps adicionales, incluido en el plan gratuito de Contact.';

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

const WalletPage = () => {
  const navigate = useNavigate();
  useSeoMeta({ title: PAGE_TITLE, description: PAGE_DESCRIPTION, path: '/wallet' });

  return (
    <div className="wa-root">
      <HeroDither/>
      <header className="wa-nav">
        <div className="wa-nav-inner">
          <ContactLogo clickable/>
          <div className="wa-nav-actions">
            <button onClick={() => navigate('/')} className="wa-btn-back">
              <IconArrowLeft/> Volver
            </button>
            <button onClick={() => navigate('/comprar')} className="wa-btn-comprar-nav">Comprar</button>
            <button onClick={() => navigate('/login')} className="wa-btn-acceder">Acceder</button>
          </div>
        </div>
      </header>

      <section className="wa-hero">
        <div className="wa-container">
          <p className="wa-eyebrow">Apple Wallet y Google Wallet</p>
          <h1 className="wa-h1">Tu perfil, guardado en la billetera de tu celular.</h1>
          <p className="wa-lead">
            Guardas tu perfil de Contact una sola vez y queda disponible en la misma billetera donde
            tienes tus tarjetas de embarque o de transporte. Un toque para abrirlo, sin buscar un link
            ni abrir una app.
          </p>
        </div>
      </section>

      <section className="wa-section">
        <div className="wa-container">
          <Reveal>
          <h2 className="wa-h2">Un pase digital, no una captura de pantalla.</h2>
          <div className="wa-grid">
            {WALLET_CARDS.map((c) => (
              <div key={c.title} className="wa-card">
                <span className="wa-card-icon">{c.icon}</span>
                <h3 className="wa-card-title">{c.title}</h3>
                <p className="wa-card-desc">{c.desc}</p>
              </div>
            ))}
          </div>
          </Reveal>
        </div>
      </section>

      <section className="wa-section">
        <div className="wa-container">
          <Reveal>
          <h2 className="wa-h2 wa-h2--tight">Preguntas frecuentes.</h2>
          <div className="wa-faq-list">
            {FAQS.map((f) => (
              <div key={f.q} className="wa-faq-item">
                <h4 className="wa-faq-q">{f.q}</h4>
                <p className="wa-faq-a">{f.a}</p>
              </div>
            ))}
          </div>
          </Reveal>
        </div>
      </section>

      <section className="wa-cta">
        <div className="wa-container wa-cta-inner">
          <h2 className="wa-h2">Agrega tu perfil a tu Wallet hoy.</h2>
          <p className="wa-p wa-cta-sub">Crea tu perfil gratis y guárdalo en tu celular en menos de un minuto.</p>
          <div className="wa-cta-actions">
            <button onClick={() => navigate('/register')} className="wa-btn-pill">
              Comenzar gratis <IconArrowRight/>
            </button>
            <button onClick={() => navigate('/cotizar')} className="wa-btn-outline">
              Hablar con ventas
            </button>
          </div>
        </div>
      </section>

      <footer className="wa-footer">
        <div className="wa-container">
          <div className="wa-related-row">
            <RelatedPagesLinks currentPath="/wallet" linkClassName="wa-related-link" />
          </div>
          <div className="wa-footer-inner">
            <ContactLogo/>
            <span className="wa-footer-copy">© 2026 CONTACT Network SPA. Chile</span>
          </div>
        </div>
      </footer>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <style>{CSS}</style>
      <style>{REVEAL_CSS}</style>
    </div>
  );
};

const CSS = `
:root{
  --wa-bg:#F9F9F8;
  --wa-bg-w:#FFFFFF;
  --wa-text:#000000;
  --wa-muted:#4a4a4a;
  --wa-faint:#C0C0C0;
  --wa-border:#EEEEEC;
  --wa-border-dk:#CCCCCA;
}

.wa-root{font-family:'abcNormal','Inter','Arial',sans-serif;background:var(--wa-bg);color:var(--wa-text);min-height:100vh;overflow-x:hidden;line-height:1.6;position:relative}
.nx-hero-dither{position:fixed;inset:0;z-index:0;overflow:hidden;pointer-events:none;opacity:.27;-webkit-mask-image:linear-gradient(180deg,#000 0%,#000 40%,transparent 85%);mask-image:linear-gradient(180deg,#000 0%,#000 40%,transparent 85%);transform:translateZ(0);will-change:transform}

.wa-nav{position:sticky;top:0;z-index:100;background:transparent}
.wa-nav-inner{max-width:900px;margin:0 auto;padding:0 40px;height:64px;display:flex;align-items:center;justify-content:space-between}
.wa-btn-back{display:inline-flex;align-items:center;gap:7px;background:none;border:none;color:var(--wa-muted);font-family:'Inter',system-ui,sans-serif;font-size:13px;font-weight:500;cursor:pointer;padding:0;transition:color .15s}
.wa-btn-back:hover{color:var(--wa-text)}
.wa-nav-actions{display:flex;align-items:center;gap:10px}
.wa-btn-acceder{display:inline-flex;align-items:center;background:var(--wa-text);color:#fff;border:none;border-radius:100px;height:36px;padding:0 18px;font-family:'Inter',system-ui,sans-serif;font-size:13px;font-weight:600;cursor:pointer;transition:opacity .15s}
.wa-btn-acceder:hover{opacity:.82}
.wa-btn-comprar-nav{display:inline-flex;align-items:center;background:transparent;color:var(--wa-text);border:1.5px solid var(--wa-border-dk);border-radius:100px;height:36px;padding:0 18px;font-family:'Inter',system-ui,sans-serif;font-size:13px;font-weight:600;cursor:pointer;transition:all .15s}
.wa-btn-comprar-nav:hover{border-color:var(--wa-text)}

.wa-container{max-width:900px;margin:0 auto;padding:0 40px}

.wa-hero{padding:72px 0 56px}
.wa-eyebrow{font-family:'Inter',system-ui,sans-serif;font-size:11px;font-weight:700;color:var(--wa-muted);text-transform:uppercase;letter-spacing:.08em;margin-bottom:20px}
.wa-h1{font-weight:300;font-size:clamp(32px,4.6vw,52px);line-height:1.15;letter-spacing:-.025em;color:var(--wa-text);margin-bottom:26px;max-width:720px}
.wa-lead{font-family:'Inter',system-ui,sans-serif;font-size:clamp(15px,1.6vw,17px);color:var(--wa-muted);line-height:1.75;max-width:640px}

.wa-section{padding:0 0 80px}
.wa-h2{font-weight:300;font-size:clamp(22px,2.6vw,30px);line-height:1.3;letter-spacing:-.02em;color:var(--wa-text);margin-bottom:32px;max-width:620px}
.wa-h2--tight{margin-bottom:28px}
.wa-p{font-family:'Inter',system-ui,sans-serif;font-size:15px;color:var(--wa-muted);line-height:1.75;max-width:560px}

.wa-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:1px;background:var(--wa-border);border:1px solid var(--wa-border)}
.wa-card{background:var(--wa-bg-w);padding:28px}
.wa-card-icon{display:inline-flex;color:var(--wa-text);margin-bottom:14px}
.wa-card-title{font-family:'Inter',system-ui,sans-serif;font-size:15px;font-weight:700;color:var(--wa-text);margin-bottom:8px;letter-spacing:-.01em}
.wa-card-desc{font-family:'Inter',system-ui,sans-serif;font-size:13.5px;color:var(--wa-muted);line-height:1.65}

.wa-faq-list{display:flex;flex-direction:column;gap:22px;max-width:640px}
.wa-faq-item{border-top:1px solid var(--wa-border);padding-top:16px}
.wa-faq-q{font-family:'Inter',system-ui,sans-serif;font-size:14.5px;font-weight:700;color:var(--wa-text);margin-bottom:8px}
.wa-faq-a{font-family:'Inter',system-ui,sans-serif;font-size:13.5px;color:var(--wa-muted);line-height:1.65}

.wa-cta{padding:0 0 80px}
.wa-cta-inner{background:var(--wa-bg-w);border:1px solid var(--wa-border);border-radius:24px;padding:60px 48px;text-align:center;display:flex;flex-direction:column;align-items:center}
.wa-cta-sub{text-align:center;margin:0 auto 32px}
.wa-cta-actions{display:flex;align-items:center;gap:14px}
.wa-btn-pill{display:inline-flex;align-items:center;gap:8px;background:var(--wa-text);color:#fff;border:none;border-radius:100px;height:48px;padding:0 24px;font-family:'Inter',system-ui,sans-serif;font-size:14px;font-weight:600;cursor:pointer;transition:opacity .15s,transform .2s cubic-bezier(.22,1,.36,1);white-space:nowrap}
.wa-btn-pill:hover{opacity:.82;transform:translateY(-1px)}
.wa-btn-outline{display:inline-flex;align-items:center;gap:8px;background:transparent;color:var(--wa-text);border:1.5px solid var(--wa-border-dk);border-radius:100px;height:48px;padding:0 24px;font-family:'Inter',system-ui,sans-serif;font-size:14px;font-weight:600;cursor:pointer;transition:all .15s;white-space:nowrap}
.wa-btn-outline:hover{border-color:var(--wa-text);background:var(--wa-bg)}

.wa-related-row{display:flex;flex-wrap:wrap;gap:10px;padding:28px 0;border-top:1px solid var(--wa-border)}
.wa-related-link{background:none;border:1px solid var(--wa-border-dk);border-radius:100px;padding:7px 15px;color:var(--wa-muted);font-family:'Inter',system-ui,sans-serif;font-size:12.5px;font-weight:600;cursor:pointer;transition:all .15s}
.wa-related-link:hover{color:var(--wa-text);border-color:var(--wa-text)}

.wa-footer-inner{display:flex;align-items:center;justify-content:space-between;padding:24px 0}
.wa-footer-copy{font-family:'Inter',system-ui,sans-serif;font-size:12px;color:var(--wa-faint)}

@media(max-width:640px){
  .wa-nav-inner,.wa-container{padding:0 20px}
  .wa-nav-actions{gap:6px}
  .wa-btn-acceder,.wa-btn-comprar-nav{height:32px;padding:0 12px;font-size:12px}
  .wa-btn-back{font-size:0}
  .wa-hero{padding:48px 0 40px}
  .wa-section,.wa-cta{padding-bottom:56px}
  .wa-grid{grid-template-columns:1fr}
  .wa-cta-inner{padding:44px 22px}
  .wa-cta-actions{flex-direction:column;width:100%}
  .wa-cta-actions .wa-btn-pill,.wa-cta-actions .wa-btn-outline{width:100%;justify-content:center}
  .wa-footer-inner{flex-direction:column;gap:8px;text-align:center}
}
@media(prefers-reduced-motion:reduce){
  *{animation-duration:.01ms !important;transition-duration:.01ms !important}
}
`;

export default WalletPage;
